import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listTeam, grantRole, revokeRole } from "@/lib/admin.roles.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/team")({ component: TeamPage });

function TeamPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listTeam);
  const grantFn = useServerFn(grantRole);
  const revokeFn = useServerFn(revokeRole);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "team"],
    queryFn: () => listFn(),
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "support">("admin");

  const grant = useMutation({
    mutationFn: () => grantFn({ data: { email: email.trim(), role } }),
    onSuccess: () => {
      toast.success(`Granted ${role} to ${email}`);
      setEmail("");
      qc.invalidateQueries({ queryKey: ["admin", "team"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (v: { userId: string; role: "admin" | "support" }) =>
      revokeFn({ data: v }),
    onSuccess: () => {
      toast.success("Role revoked");
      qc.invalidateQueries({ queryKey: ["admin", "team"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-8">
      <div>
        <p className="overline mb-2">Team</p>
        <h2 className="font-serif text-2xl text-primary mb-4">Roles &amp; access</h2>
        <p className="text-sm text-ink-secondary mb-6 max-w-2xl">
          Admins can do anything in this console. Support can view but not
          grant roles (UI for support-only views to follow). The user must
          have signed in at least once before you can grant them a role.
        </p>

        <div className="border border-border bg-card p-4 mb-8">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="flex-1 min-w-[240px]">
              <span className="overline block mb-1">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
                placeholder="person@example.com"
              />
            </label>
            <label>
              <span className="overline block mb-1">Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as "admin" | "support")}
                className="border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="admin">admin</option>
                <option value="support">support</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => grant.mutate()}
              disabled={!email.trim() || grant.isPending}
              className="bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-50"
            >
              {grant.isPending ? "Granting…" : "Grant role"}
            </button>
          </div>
        </div>
      </div>

      {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
      {error && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load team"}
        </p>
      )}

      {data && (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-ink-tertiary">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Roles</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.user_id} className="border-t border-border">
                  <td className="p-3">{m.display_name ?? "—"}</td>
                  <td className="p-3 font-mono text-xs">{m.email ?? "—"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      {m.roles.map((r) => (
                        <span
                          key={r}
                          className="px-2 py-0.5 text-xs font-mono border border-border"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      {m.roles.map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() =>
                            revoke.mutate({ userId: m.user_id, role: r as "admin" | "support" })
                          }
                          disabled={revoke.isPending}
                          className="text-xs text-destructive underline underline-offset-4"
                        >
                          Revoke {r}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {data.members.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-ink-tertiary text-sm">
                    No team members with assigned roles yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
